import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Flame, Sunrise, UserSearch, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import CRMCompanyDetail from './CRMCompanyDetail';
import CRMEnrichmentQueue from './CRMEnrichmentQueue';
import CRMOutreachModal from './CRMOutreachModal';
import CRMTodayWorklist from './CRMTodayWorklist';
import CRMScheduledSends from './CRMScheduledSends';
import { CRMOpportunityCard } from './CRMOpportunityCard';
import { runOpportunityAction } from './crmOpportunityActions';
import { useGraphStatus, GraphMailboxBanner, connectMicrosoftMailbox } from '../components/GraphMailboxStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKETS = [
  { key: '', label: 'All' },
  { key: 'ready_for_outreach', label: 'Ready for Outreach' },
  { key: 'needs_enrichment', label: 'Needs Enrichment' },
  { key: 'followup_overdue', label: 'Follow-Up Overdue' },
  { key: 'followup_due_today', label: 'Follow-Up Due Today' },
  { key: 'awaiting_response', label: 'Awaiting Response' },
  { key: 'response_needed', label: 'Responses Needing Action' },
  { key: 'discovery_needed', label: 'Discovery Needed' },
  { key: 'agreement_opportunity', label: 'Agreement Opportunity' },
  { key: 'existing_client', label: 'Existing Clients' },
];

const CRMOpportunities = ({ embedded = false }) => {
  const [data, setData] = useState({ opportunities: [], total_eligible: 0, bucket_counts: {} });
  const [config, setConfig] = useState({ pipeline_stages: [], account_statuses: [], lead_sources: [] });
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState('');
  const [view, setView] = useState('today');
  const [selectedId, setSelectedId] = useState(null);
  const [outreachFor, setOutreachFor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnecting, setDisconnecting] = useState(false);
  const { status: graphStatus, reload: reloadGraphStatus } = useGraphStatus();

  const bumpRefresh = () => setRefreshKey(k => k + 1);

  useEffect(() => { fetchWithAuth(`${API_URL}/api/crm/config`).then(setConfig).catch(() => {}); }, []);

  useEffect(() => {
    const connected = searchParams.get('graph_connected');
    const err = searchParams.get('graph_error');
    if (connected) {
      toast.success('Outlook mailbox connected');
      reloadGraphStatus();
    }
    if (err) toast.error(err);
    if (connected || err) {
      const next = new URLSearchParams(searchParams);
      next.delete('graph_connected'); next.delete('graph_error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, reloadGraphStatus]);

  const disconnectMailbox = async () => {
    setDisconnecting(true);
    try {
      await fetchWithAuth(`${API_URL}/api/graph/disconnect`, { method: 'POST' });
      toast.success('Outlook mailbox disconnected');
      reloadGraphStatus();
    } catch (e) {
      toast.error('Could not disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100 });
    if (bucket) params.set('bucket', bucket);
    fetchWithAuth(`${API_URL}/api/crm/opportunities?${params.toString()}`)
      .then(setData).catch(() => toast.error('Could not load opportunities')).finally(() => setLoading(false));
  }, [bucket]);

  useEffect(() => { if (view === 'queue') load(); }, [load, view, refreshKey]);

  const runAction = async (opp, actionKey, extra) => {
    try {
      await runOpportunityAction(API_URL, fetchWithAuth, opp, actionKey, extra);
      toast.success('Updated');
      bumpRefresh();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    }
  };

  const openOutreach = (opp, draftType) => setOutreachFor({ opp, draftType });

  return (
    <IntelligenceLayout title="Opportunities" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-opportunities-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1F44] flex items-center gap-2" data-testid="page-title">
            <Flame className="h-5 w-5 text-[#D4AF37]" /> Opportunities
          </h1>
          <p className="text-sm text-gray-500 mt-1">{data.total_eligible ? `${data.total_eligible.toLocaleString()} actionable companies — ` : ''}who to contact today, why, and what's next</p>
        </div>

        <GraphMailboxBanner status={graphStatus} onConnect={connectMicrosoftMailbox} onDisconnect={disconnectMailbox} disconnecting={disconnecting} />

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="opportunities-view-toggle">
          <button onClick={() => setView('today')} data-testid="opportunities-view-today"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'today' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>
            <Sunrise className="h-3.5 w-3.5" /> Today
          </button>
          <button onClick={() => setView('queue')} data-testid="opportunities-view-queue"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'queue' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>
            <Flame className="h-3.5 w-3.5" /> Priority Queue
          </button>
          <button onClick={() => setView('scheduled')} data-testid="opportunities-view-scheduled"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'scheduled' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>
            <CalendarClock className="h-3.5 w-3.5" /> Scheduled Sends
          </button>
          <button onClick={() => setView('enrich')} data-testid="opportunities-view-enrich"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'enrich' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>
            <UserSearch className="h-3.5 w-3.5" /> Enrich Decision Makers
          </button>
        </div>

        {view === 'scheduled' && (
          <CRMScheduledSends onOpenCompany={setSelectedId} />
        )}

        {view === 'today' && (
          <CRMTodayWorklist onOpenCompany={setSelectedId} onOutreach={openOutreach} refreshKey={refreshKey} bumpRefresh={bumpRefresh} />
        )}

        {view === 'enrich' && (
          <CRMEnrichmentQueue onOpenCompany={setSelectedId} />
        )}

        {view === 'queue' && (
        <>
        <div className="flex flex-wrap gap-2" data-testid="opportunity-bucket-filters">
          {BUCKETS.map(b => (
            <button key={b.key || 'all'} onClick={() => setBucket(b.key)} data-testid={`bucket-filter-${b.key || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                bucket === b.key ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A1F44]/40'
              }`}>
              {b.label}{b.key && ` (${data.bucket_counts[b.key] ?? 0})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : data.opportunities.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm" data-testid="opportunities-empty-state">No companies in this view right now.</div>
        ) : (
          <div className="space-y-3" data-testid="opportunities-list">
            {data.opportunities.map(opp => (
              <CRMOpportunityCard key={opp.id} opp={opp} onOpenCompany={setSelectedId} onOutreach={openOutreach} onRunAction={runAction} />
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {selectedId && (
        <CRMCompanyDetail companyId={selectedId} config={config} onClose={() => setSelectedId(null)} onUpdated={bumpRefresh} />
      )}

      {outreachFor && (
        <CRMOutreachModal
          company={outreachFor.opp}
          contact={outreachFor.opp.best_contact}
          initialDraftType={outreachFor.draftType}
          onClose={() => setOutreachFor(null)}
          onLogged={bumpRefresh}
        />
      )}
    </IntelligenceLayout>
  );
};

export default CRMOpportunities;
